import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgendaEvento, Contato } from '@eleg360/types';
import { agendaApi } from '@/lib/api/agenda';
import { adminApi } from '@/lib/api/admin';
import { contatosApi } from '@/lib/api/contatos';
import { liderancasApi } from '@/lib/api/liderancas';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { ROTULO_BASE_LEGAL } from '@/lib/format';
import { BASE_LEGAL_ENVIO, motivoBloqueio, podeEnviarWhatsApp } from '@/lib/lgpd';
import { Info } from '@/design-system/Info';

/**
 * Motor de convite a moradores da localidade (proto:1265-1281).
 *
 * Duas regras do protótipo são de governança, não de UX:
 *
 * 1. Só existe público quando o compromisso tem localidade. Sem ela, a nota
 *    literal de "Compromisso interno" ocupa o lugar do motor.
 * 2. Acima do limiar do mandato o envio NÃO acontece: vira decisão da Chefia,
 *    em Campanhas. O limiar é o mesmo que o servidor aplica ao criar campanha
 *    (`campanhas.service.ts:50`, `tenant.configuracoes.limiar_campanha`) — aqui
 *    ele é lido do mandato, nunca cravado.
 *
 * A base legal não é reimplementada: quem decide é `lib/lgpd`, a mesma função
 * que decide o botão de WhatsApp do contato e o público real do servidor.
 */

/** Teto da lista do protótipo; acima disso ele pede para refinar a busca. */
const LIMITE_LISTA = 120;

/** Página usada para montar as redes de liderança e o total de opt-in da localidade. */
const LIMITE_OPTIN_LOCALIDADE = 300;

/**
 * Limiar aplicado pelo servidor quando o mandato não configurou nenhum
 * (`campanhas.service.ts:50`). Não é um número escolhido por esta tela: é o
 * mesmo padrão que decidirá o status da campanha do outro lado.
 */
const LIMIAR_PADRAO_SERVIDOR = 100;

type Escala = 'bairro' | 'mun' | 'uf' | 'todos';
type ModoFiltro = 'optin' | 'lid' | 'todos';

const primeiroNome = (n: string) => n.trim().split(/\s+/)[0] || n;

export interface MotorConviteProps {
  evento: AgendaEvento;
}

export function MotorConvite({ evento }: MotorConviteProps) {
  const token = useAuthStore((s) => s.token) || '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  const temLocalidade = !!evento.bairro;

  const [escala, setEscala] = useState<Escala>('bairro');
  const [bairroRecorte, setBairroRecorte] = useState('');
  const [uf, setUf] = useState(evento.uf || '');
  const [municipio, setMunicipio] = useState(evento.municipio || '');
  const [modo, setModo] = useState<ModoFiltro>('optin');
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [mensagem, setMensagem] = useState<string | null>(null);

  const bairroAlvo = bairroRecorte || evento.bairro || '';

  const ufs = useMemo(() => [...new Set(bairros.map((b) => b.uf).filter(Boolean))], [bairros]);

  // O compromisso nem sempre traz UF/município. Quando não traz, o valor
  // efetivo é o primeiro da lista — o MESMO que o `<select>` exibiria. Manter
  // o estado vazio fazia a tela mostrar "CE" e consultar o Brasil inteiro:
  // o recorte exibido tem de ser o recorte consultado.
  const ufSel = uf || ufs[0] || '';
  const municipios = useMemo(
    () => [...new Set(bairros.filter((b) => !ufSel || b.uf === ufSel).map((b) => b.municipio).filter(Boolean))],
    [bairros, ufSel],
  );
  const municipioSel = municipio || municipios[0] || '';

  // Recorte: o filtro territorial é do servidor (contatos.service.ts:95-116),
  // como em qualquer outra tela de público — não uma filtragem local de uma
  // página já baixada, que mentiria no total.
  const filtros: Record<string, any> = { limit: LIMITE_LISTA, sort: 'nome' };
  if (escala === 'bairro') filtros.bairro = bairroAlvo;
  if (escala === 'mun') {
    filtros.municipio = municipioSel;
    filtros.uf = ufSel;
  }
  if (escala === 'uf') filtros.uf = ufSel;
  if (modo === 'optin') filtros.base_legal = BASE_LEGAL_ENVIO;
  if (busca.trim()) filtros.search = busca.trim();

  const { data: recorte } = useQuery({
    queryKey: ['contatos', 'convite-recorte', filtros],
    queryFn: () => contatosApi.list(filtros, token),
    enabled: !!token && temLocalidade,
  });

  const { data: naLocalidade } = useQuery({
    queryKey: ['contatos', 'convite-localidade', evento.bairro],
    queryFn: () => contatosApi.list({ bairro: evento.bairro, limit: 1 }, token),
    enabled: !!token && temLocalidade,
  });

  const { data: optinLocalidade } = useQuery({
    queryKey: ['contatos', 'convite-optin', evento.bairro],
    queryFn: () =>
      contatosApi.list(
        { bairro: evento.bairro, base_legal: BASE_LEGAL_ENVIO, limit: LIMITE_OPTIN_LOCALIDADE },
        token,
      ),
    enabled: !!token && temLocalidade,
  });

  const { data: liderancas = [] } = useQuery({
    queryKey: ['liderancas', 'convite', evento.bairro],
    queryFn: () => liderancasApi.list({ bairro: evento.bairro }, token),
    enabled: !!token && temLocalidade,
  });

  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
  });

  const limiarConfigurado = mandato?.configuracoes?.limiar_campanha;
  const limiar = limiarConfigurado ?? LIMIAR_PADRAO_SERVIDOR;

  const itens: Contato[] = recorte?.items ?? [];
  // "Só redes de liderança" não tem filtro no servidor (não existe parâmetro de
  // liderança em `FilterContatoDto`), então é o único que roda aqui.
  const lista = modo === 'lid' ? itens.filter((c) => !!c.lideranca_id) : itens;
  const totalRecorte = modo === 'lid' ? lista.length : recorte?.total ?? 0;
  const excedentes = modo === 'lid' ? 0 : Math.max(0, totalRecorte - lista.length);

  const ativas = liderancas.filter((l) => l.status === 'ativa');
  const optinsApurados = optinLocalidade?.items ?? [];
  // A rede de cada liderança é montada sobre UMA página de opt-ins. Quando a
  // localidade não cabe na página, a contagem do chip é um piso, não o total —
  // e isso precisa estar escrito, como já está no filtro "Só redes de
  // liderança" logo abaixo da lista.
  const redeParcial = (optinLocalidade?.total ?? 0) > optinsApurados.length;
  const convidaveisDaLocalidade = optinsApurados.filter(podeEnviarWhatsApp);
  const redeDe = (idLideranca: string) =>
    convidaveisDaLocalidade.filter((c) => c.lideranca_id === idLideranca);

  const selecionados = Object.keys(sel).filter((k) => sel[k]);

  const acimaDoLimiar = selecionados.length > limiar;

  const atualizar = useMutation({
    mutationFn: (convites: string[]) => agendaApi.update(evento.id, { convites }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
      if (acimaDoLimiar) {
        toast(
          `${selecionados.length} convite(s) registrados no compromisso · acima do limiar de ${limiar}, o disparo não sai daqui: abrimos Campanhas em "Nova campanha" com o território e a mensagem já carregados, para a aprovação da Chefia.`,
        );
        // proto:1281 leva `STATE.camp.terr` e `STATE.camp.msg` junto. Sem isso
        // a pessoa chega numa tela vazia e refaz bairro e mensagem do zero.
        navigate('/campanhas', {
          state: {
            origem: 'agenda-convite',
            bairro: bairroAlvo,
            mensagem: mensagem ?? textoPadrao,
            compromisso: evento.titulo,
            destinatarios: selecionados.length,
          },
        });
        return;
      }
      toast(`${selecionados.length} convite(s) registrados no compromisso.`);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast(Array.isArray(msg) ? msg.join(' · ') : msg || 'Erro ao registrar os convites.');
    },
  });

  const registrar = () => {
    if (!selecionados.length) {
      toast('Selecione ao menos um contato');
      return;
    }
    // Governança do protótipo (proto:1281): acima do limiar o que não acontece
    // é o ENVIO — o registro acontece sempre. Lá `a.convidados` é gravado ANTES
    // do `if(sel.length>STATE.limiar)`. Descartar a seleção aqui fazia quem
    // escolheu 200 pessoas perder as 200 e a mensagem digitada junto; o desvio
    // para a aprovação da Chefia é feito depois de gravar (ver `onSuccess`).
    atualizar.mutate([...new Set([...(evento.convites || []), ...selecionados])]);
  };

  const textoPadrao = useMemo(() => {
    const quem = mandato ? `${mandato.cargo} ${mandato.nome}` : 'O gabinete';
    const quando = `${new Date(evento.inicio).toLocaleDateString('pt-BR')} às ${new Date(
      evento.inicio,
    ).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    return `Olá, {{primeiro_nome}}! ${quem} estará em ${evento.bairro} em ${quando}: ${evento.titulo}${
      evento.endereco ? `, no(a) ${evento.endereco}` : ''
    }. Sua presença faz diferença! Responda SAIR para não receber mais mensagens.`;
  }, [mandato, evento]);

  if (!temLocalidade) {
    return (
      <div className="note" style={{ marginTop: 12 }}>
        <b>Compromisso interno</b>
        Sem localidade vinculada, não há público para convidar. Edite e marque como público para
        habilitar o envio.
      </div>
    );
  }

  const trocarEscala = (nova: Escala, outroBairro: boolean) => {
    setEscala(nova);
    if (nova === 'bairro') {
      setBairroRecorte(
        outroBairro
          ? bairroRecorte && bairroRecorte !== evento.bairro
            ? bairroRecorte
            : bairros.find((b) => b.n !== evento.bairro)?.n || ''
          : '',
      );
    }
  };

  const escalaAtiva = (chave: string) => {
    const outro = escala === 'bairro' && !!bairroRecorte && bairroRecorte !== evento.bairro;
    if (chave === 'bairro') return escala === 'bairro' && !outro;
    if (chave === 'outro') return outro;
    return escala === chave;
  };

  const CHIPS: { chave: string; rotulo: string; aplicar: () => void }[] = [
    { chave: 'bairro', rotulo: 'Bairro do evento', aplicar: () => trocarEscala('bairro', false) },
    { chave: 'outro', rotulo: 'Outro bairro', aplicar: () => trocarEscala('bairro', true) },
    { chave: 'mun', rotulo: 'Município', aplicar: () => trocarEscala('mun', false) },
    { chave: 'uf', rotulo: 'Estado', aplicar: () => trocarEscala('uf', false) },
    { chave: 'todos', rotulo: 'Toda a base', aplicar: () => trocarEscala('todos', false) },
  ];

  return (
    <div className="card flat" style={{ marginTop: 12 }}>
      <h3>Convidar moradores da localidade</h3>

      <div className="stat-row" style={{ margin: '6px 0' }}>
        <span data-testid="estat-recorte">
          <b>{totalRecorte}</b> no recorte
        </span>
        <span data-testid="estat-localidade">
          <b>{naLocalidade?.total ?? 0}</b> contatos em {evento.bairro}
        </span>
        <span data-testid="estat-optin">
          <b>{optinLocalidade?.total ?? 0}</b> com opt-in
        </span>
        <span data-testid="estat-liderancas">
          <b>{ativas.length}</b> lideranças ativas
        </span>
      </div>

      {ativas.length > 0 && (
        <div className="filters" style={{ marginBottom: 8 }}>
          {ativas.map((l) => {
            const rede = redeDe(l.id);
            return (
              <button
                key={l.id}
                type="button"
                className="chip acc chipbtn"
                title="Contatos com opt-in vinculados a esta liderança; os sem opt-in não entram"
                onClick={() =>
                  setSel((s) => {
                    const novo = { ...s };
                    for (const c of rede) novo[c.id] = true;
                    return novo;
                  })
                }
              >
                + rede de {primeiroNome(l.nome)} ({rede.length}
                {redeParcial ? '+' : ''})
              </button>
            );
          })}
        </div>
      )}

      {ativas.length > 0 && redeParcial && (
        <p
          style={{ fontSize: 12, color: 'var(--ink3)', margin: '0 0 8px' }}
          data-testid="nota-rede-parcial"
        >
          São {optinLocalidade?.total} pessoas com opt-in em {evento.bairro} e a consulta traz{' '}
          {LIMITE_OPTIN_LOCALIDADE} por vez: as redes acima foram apuradas sobre as{' '}
          {optinsApurados.length} primeiras, então os números são um piso. Use o recorte por bairro
          e a busca por nome para alcançar as demais.
        </p>
      )}

      <div className="filters" style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Abrangência:</span>
        {CHIPS.map((c) => (
          <button
            key={c.chave}
            type="button"
            className={`chip neutral chipbtn ${escalaAtiva(c.chave) ? 'on' : ''}`}
            onClick={c.aplicar}
          >
            {c.rotulo}
          </button>
        ))}
      </div>

      {escalaAtiva('outro') && (
        <div className="filters" style={{ marginBottom: 6 }}>
          <select
            className="sel"
            aria-label="Bairro do recorte"
            value={bairroRecorte}
            onChange={(e) => setBairroRecorte(e.target.value)}
          >
            {bairros.map((b) => (
              <option key={b.id} value={b.n}>
                {b.n}
              </option>
            ))}
          </select>
        </div>
      )}

      {(escala === 'mun' || escala === 'uf') && (
        <div className="filters" style={{ marginBottom: 6 }}>
          <select
            className="sel"
            aria-label="UF do recorte"
            value={ufSel}
            onChange={(e) => {
              setUf(e.target.value);
              const primeiro = bairros.find((b) => b.uf === e.target.value)?.municipio || '';
              setMunicipio(primeiro);
            }}
          >
            {ufs.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          {escala === 'mun' && (
            <select
              className="sel"
              aria-label="Município do recorte"
              value={municipioSel}
              onChange={(e) => setMunicipio(e.target.value)}
            >
              {municipios.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="filters">
        <input
          className="in"
          style={{ width: 180, padding: '5px 9px' }}
          placeholder="Buscar nome…"
          aria-label="Buscar nome"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className="sel"
          aria-label="Filtro de base legal"
          value={modo}
          onChange={(e) => setModo(e.target.value as ModoFiltro)}
        >
          <option value="optin">Só com opt-in</option>
          <option value="lid">Só redes de liderança</option>
          <option value="todos">Todos (mostra bloqueados)</option>
        </select>
        <button
          type="button"
          className="btn sm"
          onClick={() =>
            setSel((s) => {
              const novo = { ...s };
              // Só quem pode receber: a decisão é de `lib/lgpd`, não daqui.
              for (const c of lista) if (podeEnviarWhatsApp(c)) novo[c.id] = true;
              return novo;
            })
          }
        >
          Selecionar todos
        </button>
        <button type="button" className="btn sm" onClick={() => setSel({})}>
          Limpar
        </button>
      </div>

      <div
        style={{
          maxHeight: 220,
          overflow: 'auto',
          border: '1px solid var(--line)',
          borderRadius: 8,
          marginTop: 8,
        }}
      >
        {lista.map((c) => {
          const ok = podeEnviarWhatsApp(c);
          return (
            <label
              key={c.id}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '6px 10px',
                borderBottom: '1px solid var(--line)',
                fontSize: 13,
                opacity: ok ? 1 : 0.5,
              }}
              title={motivoBloqueio(c) || undefined}
            >
              <input
                type="checkbox"
                checked={!!sel[c.id]}
                disabled={!ok}
                onChange={(e) => setSel((s) => ({ ...s, [c.id]: e.target.checked }))}
              />
              <span>{c.nome}</span>
              {escala !== 'bairro' && c.bairro && (
                <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{c.bairro}</span>
              )}
              <span style={{ marginLeft: 'auto' }}>
                <span
                  className={`chip ${
                    c.base_legal === 'optin' ? 'good' : c.base_legal === 'optout' ? 'crit' : 'neutral'
                  }`}
                >
                  {ROTULO_BASE_LEGAL[c.base_legal] || c.base_legal}
                </span>
              </span>
            </label>
          );
        })}
        {lista.length === 0 && <div className="empty">ninguém com esse filtro</div>}
        {excedentes > 0 && <div className="empty">+{excedentes}, refine a busca</div>}
      </div>

      {modo === 'lid' && (recorte?.total ?? 0) > itens.length && (
        <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '6px 0 0' }}>
          O recorte tem {recorte?.total} contatos e a API não filtra por liderança: a rede foi
          apurada sobre os {itens.length} primeiros. Refine a busca para conferir os demais.
        </p>
      )}

      <p style={{ fontSize: 12, color: 'var(--ink2)', margin: '8px 0 0' }} data-testid="rodape-selecao">
        <b>{selecionados.length}</b> selecionado(s) · só contatos com opt-in podem receber
      </p>

      <div className="field" style={{ marginTop: 8 }}>
        {/*
          O protótipo rotula este campo como "modelo convite_evento_v1". Não há
          catálogo de modelos no sistema — `campanha.template_id` é texto livre e
          nenhuma tabela de templates existe —, então o rótulo não afirma um
          modelo homologado que ninguém cadastrou.
        */}
        <label htmlFor="cv-msg">Mensagem do convite (rascunho · editável)</label>
        <textarea
          id="cv-msg"
          className="in"
          style={{ minHeight: 90 }}
          value={mensagem ?? textoPadrao}
          onChange={(e) => setMensagem(e.target.value)}
        />
      </div>

      {/*
        Fica visível: sem isto, o botão "Registrar convites" e a caixa de
        mensagem acima se leem como um disparo de WhatsApp. O detalhe de por que
        o motor não aceita seleção nominal vai no `Info`.
      */}
      <div className="note warn" style={{ marginTop: 8, fontSize: 12 }} data-testid="nota-disparo">
        <b>O convite não sai por WhatsApp a partir daqui.</b> Os convites ficam registrados no
        compromisso; o disparo é feito em Campanhas, para o recorte inteiro com opt-in.{' '}
        <Info titulo="Por que o disparo não acontece nesta tela">
          O único motor de disparo em massa é <code>POST /gab/campanhas</code> (mensagem avulsa
          também sai da régua de solicitação concluída,{' '}
          <code>solicitacoes.service.ts:283</code>), e ele recalcula o público no servidor a partir
          de <code>publico_def</code>, que aceita bairro, tag, município, UF e amostra (
          <code>campanhas.service.ts:266</code>): uma seleção nominal de contatos não é
          representável nele. O texto da mensagem acima é rascunho de tela, porque não há campo de
          mensagem em <code>gab.agenda_evento</code> para guardá-lo.
        </Info>
      </div>

      <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '8px 0 0' }} data-testid="nota-limiar">
        Limiar de aprovação: {limiar} destinatários{' '}
        {limiarConfigurado === undefined
          ? '(padrão do servidor: nenhum limiar_campanha configurado em Administração › Limiares)'
          : '(configuração do mandato)'}
        . Acima dele o envio não acontece aqui: vai para aprovação da Chefia, em Campanhas.
      </p>

      <div className="filters" style={{ marginTop: 10 }}>
        <button
          type="button"
          className="btn pri"
          disabled={atualizar.isPending}
          onClick={registrar}
        >
          Registrar convites ({selecionados.length})
        </button>
      </div>
    </div>
  );
}
